/**
 * Server functions for the town content pipeline.
 *
 * Thin wrappers only — all runtime helpers live in `townContent.server.ts`.
 */

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { normalizeTownContent, type TownContentRun } from "@/lib/townContent";

type AdminContext = {
  supabase: {
    rpc: (
      fn: "has_role",
      args: { _user_id: string; _role: "admin" },
    ) => PromiseLike<{ data: boolean | null; error: { message: string } | null }>;
  };
  userId: string;
};

async function requireAdmin(context: AdminContext) {
  const { data: isAdmin, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error(`Unable to verify admin access: ${error.message}`);
  if (!isAdmin) throw new Error("Forbidden");
}

/** Public: approved copy for one page. Safe to call from a public route loader. */
export const getApprovedTownContent = createServerFn({ method: "GET" })
  .inputValidator(
    (input: {
      stateSlug: string;
      townSlug: string;
      serviceSlug?: string | null;
    }) => input,
  )
  .handler(async ({ data }) => {
    const contentColumns =
      "id,state_slug,town_slug,service_slug,intro,local_conditions,terrain_notes,service_narrative,nearby_context,common_mistakes,faqs,status,model,generated_at,generation_notes,edited_manually,overlap_score,reviewed_by_email,reviewed_at,updated_at";
    const url = process.env["SUPABASE_URL"];
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
    if (!url || !key) return null;

    const client = createClient(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input: RequestInfo | URL, init?: RequestInit) => {
          const headers = new Headers(init?.headers);
          if (
            key.startsWith("sb_") &&
            headers.get("Authorization") === `Bearer ${key}`
          ) {
            headers.delete("Authorization");
          }
          headers.set("apikey", key);
          return fetch(input, { ...init, headers });
        },
      },
    });

    let query = client
      .from("town_content")
      .select(contentColumns)
      .eq("state_slug", data.stateSlug)
      .eq("town_slug", data.townSlug)
      .eq("status", "approved");
    query = data.serviceSlug
      ? query.eq("service_slug", data.serviceSlug)
      : query.is("service_slug", null);

    const { data: row, error } = await query.maybeSingle();
    if (error || !row) return null;
    return normalizeTownContent(row as unknown as Record<string, unknown>);
  });

/** Admin: every content row plus the most recent generation runs. */
export const adminListTownContent = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const contentColumns =
      "id,state_slug,town_slug,service_slug,intro,local_conditions,terrain_notes,service_narrative,nearby_context,common_mistakes,faqs,status,model,generated_at,generation_notes,edited_manually,overlap_score,reviewed_by_email,reviewed_at,updated_at";
    await requireAdmin(context);

    const [rowsRes, runsRes] = await Promise.all([
      context.supabase
        .from("town_content")
        .select(contentColumns)
        .order("updated_at", { ascending: false }),
      context.supabase
        .from("town_content_runs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10),
    ]);
    if (rowsRes.error) throw new Error(rowsRes.error.message);

    return {
      rows: (rowsRes.data ?? []).map((r) =>
        normalizeTownContent(r as unknown as Record<string, unknown>),
      ),
      runs: ((runsRes.data ?? []) as unknown as TownContentRun[]).map((r) => ({
        id: r.id,
        scope: r.scope,
        state_slug: r.state_slug,
        service_slug: r.service_slug,
        status: r.status,
        total_count: r.total_count,
        completed_count: r.completed_count,
        failed_count: r.failed_count,
        model: r.model,
        error: r.error,
        triggered_by_email: r.triggered_by_email,
        created_at: r.created_at,
        updated_at: r.updated_at,
      })),
    };
  });

export interface TownDirectionRecord {
  state_slug: string;
  town_slug: string;
  /** null = applies to the whole town */
  service_slug: string | null;
  direction: string;
  updated_by_email: string | null;
  updated_at: string;
}

/** Admin: saved writer direction for every town/page in a state. */
export const adminListTownDirections = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { stateSlug: string }) => input)
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const { data: rows, error } = await context.supabase
      .from("town_content_directions")
      .select(
        "state_slug,town_slug,service_slug,direction,updated_by_email,updated_at",
      )
      .eq("state_slug", data.stateSlug);
    if (error) throw new Error(error.message);
    return { directions: (rows ?? []) as unknown as TownDirectionRecord[] };
  });

/** Admin: save (or clear, with an empty string) the direction for a town or one of its pages. */
export const adminSetTownDirection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      stateSlug: string;
      townSlug: string;
      serviceSlug?: string | null;
      direction: string;
    }) => {
      if (!input.stateSlug || !input.townSlug) throw new Error("Missing town");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const serviceSlug = data.serviceSlug ?? null;
    const text = data.direction.trim();

    if (!text) {
      let del = context.supabase
        .from("town_content_directions")
        .delete()
        .eq("state_slug", data.stateSlug)
        .eq("town_slug", data.townSlug);
      del = serviceSlug
        ? del.eq("service_slug", serviceSlug)
        : del.is("service_slug", null);
      const { error } = await del;
      if (error) throw new Error(error.message);
      return { ok: true, cleared: true };
    }

    const { error } = await context.supabase
      .from("town_content_directions")
      .upsert(
        {
          state_slug: data.stateSlug,
          town_slug: data.townSlug,
          service_slug: serviceSlug,
          direction: text,
          updated_by: context.userId,
          updated_by_email:
            (context.claims as { email?: string } | null)?.email ?? null,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "state_slug,town_slug,service_slug",
          ignoreDuplicates: false,
        },
      );
    if (error) throw new Error(error.message);
    return { ok: true, cleared: false };
  });

/** Admin: generate drafts for a chunk of towns. The client loops chunks so runs are resumable. */
export const adminGenerateTownContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      stateSlug: string;
      townSlugs: string[];
      /** One-off town-level direction for this run; overrides the saved town direction. */
      notes?: string | undefined;
      /** One-off per-page direction for this run; overrides the saved page direction. */
      pageNotes?: Record<string, string> | undefined;
      runId?: string | null | undefined;
      totalCount?: number | undefined;
      /** Limit this request to one group of pages (for per-section progress). */
      keys?: string[] | undefined;
      /** Only the last group of a town counts toward the run tally. */
      countTowns?: boolean | undefined;
    }) => {
      if (!input.townSlugs?.length) throw new Error("No towns selected");
      if (input.townSlugs.length > 4)
        throw new Error("Generate at most 4 towns per request");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const apiKey = process.env["LOVABLE_API_KEY"];
    if (!apiKey) throw new Error("Missing LOVABLE_API_KEY");

    const {
      generateTownSections,
      TOWN_CONTENT_MODEL,
      TOWN_CONTENT_PROMPT_VERSION,
    } = await import("@/lib/townContent.server");

    const actorEmail =
      (context.claims as { email?: string } | null)?.email ?? null;

    // Saved direction for every town in this request, town-level and per page.
    const { data: directionRows } = await context.supabase
      .from("town_content_directions")
      .select("town_slug,service_slug,direction")
      .eq("state_slug", data.stateSlug)
      .in("town_slug", data.townSlugs);
    const saved = (directionRows ?? []) as unknown as {
      town_slug: string;
      service_slug: string | null;
      direction: string;
    }[];

    const directionFor = (townSlug: string, serviceSlug: string | null) =>
      saved.find(
        (d) => d.town_slug === townSlug && d.service_slug === serviceSlug,
      )?.direction ?? "";

    let runId = data.runId ?? null;
    if (!runId) {
      const { data: run, error } = await context.supabase
        .from("town_content_runs")
        .insert({
          scope: data.stateSlug,
          state_slug: data.stateSlug,
          status: "running",
          total_count: data.totalCount ?? data.townSlugs.length,
          model: TOWN_CONTENT_MODEL,
          triggered_by: context.userId,
          triggered_by_email: actorEmail,
          notes: data.notes ?? null,
        })
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      runId = (run as { id: string }).id;
    }

    const results: {
      townSlug: string;
      ok: boolean;
      error?: string;
      sections?: number;
      keys?: string[];
    }[] = [];

    for (const townSlug of data.townSlugs) {
      const startedAt = Date.now();
      // Town direction (run override wins) plus per-page direction for each key.
      const townNote = (data.notes ?? directionFor(townSlug, null)).trim();
      const keys = data.keys ?? [
        "town",
        "dog-fence",
        "deer-fence",
        "garden-fence",
        "fence-restoration",
      ];
      const keyNotes: Record<string, string> = {};
      for (const key of keys) {
        const override = data.pageNotes?.[key]?.trim();
        const note =
          override ??
          directionFor(townSlug, key === "town" ? null : key).trim();
        if (note && !(key === "town" && note === townNote))
          keyNotes[key] = note;
      }
      const auditNote =
        [
          townNote ? `All pages: ${townNote}` : "",
          ...Object.entries(keyNotes).map(([k, v]) => `${k}: ${v}`),
        ]
          .filter(Boolean)
          .join(" | ") || null;

      try {
        const sections = await generateTownSections(
          data.stateSlug,
          townSlug,
          apiKey,
          townNote || undefined,
          data.keys,
          keyNotes,
        );

        const rows = Object.entries(sections).map(([key, section]) => ({
          state_slug: data.stateSlug,
          town_slug: townSlug,
          service_slug: key === "town" ? null : key,
          intro: section.intro,
          local_conditions: section.local_conditions,
          terrain_notes: section.terrain_notes,
          service_narrative: section.service_narrative,
          nearby_context: section.nearby_context,
          common_mistakes: section.common_mistakes,
          faqs: section.faqs as unknown as Record<string, string>[],
          status: "draft",
          model: TOWN_CONTENT_MODEL,
          generated_at: new Date().toISOString(),
          generation_notes: keyNotes[key]
            ? [townNote, keyNotes[key]].filter(Boolean).join(" | ")
            : townNote || null,
          edited_manually: false,
        }));

        const { error } = await context.supabase
          .from("town_content")
          .upsert(rows, {
            onConflict: "state_slug,town_slug,service_slug",
            ignoreDuplicates: false,
          });
        if (error) throw new Error(error.message);

        results.push({
          townSlug,
          ok: true,
          sections: rows.length,
          keys: Object.keys(sections),
        });

        await context.supabase.from("town_content_audit").insert({
          run_id: runId,
          state_slug: data.stateSlug,
          town_slug: townSlug,
          service_keys: Object.keys(sections),
          action: "generate",
          status: "success",
          model: TOWN_CONTENT_MODEL,
          prompt_version: TOWN_CONTENT_PROMPT_VERSION,
          prompt_notes: auditNote,
          sections_written: rows.length,
          duration_ms: Date.now() - startedAt,
          actor_id: context.userId,
          actor_email: actorEmail,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        results.push({ townSlug, ok: false, error: message });

        await context.supabase.from("town_content_audit").insert({
          run_id: runId,
          state_slug: data.stateSlug,
          town_slug: townSlug,
          service_keys: data.keys ?? [],
          action: "generate",
          status: "failed",
          model: TOWN_CONTENT_MODEL,
          prompt_version: TOWN_CONTENT_PROMPT_VERSION,
          prompt_notes: auditNote,
          sections_written: 0,
          duration_ms: Date.now() - startedAt,
          error: message.slice(0, 500),
          actor_id: context.userId,
          actor_email: actorEmail,
        });
      }
    }

    if (data.countTowns === false) {
      return { runId, results };
    }

    const completed = results.filter((r) => r.ok).length;
    const failed = results.length - completed;

    const { data: current } = await context.supabase
      .from("town_content_runs")
      .select("completed_count,failed_count,total_count")
      .eq("id", runId)
      .single();
    const prev = (current ?? {
      completed_count: 0,
      failed_count: 0,
      total_count: 0,
    }) as {
      completed_count: number;
      failed_count: number;
      total_count: number;
    };
    const nextCompleted = prev.completed_count + completed;
    const nextFailed = prev.failed_count + failed;

    await context.supabase
      .from("town_content_runs")
      .update({
        completed_count: nextCompleted,
        failed_count: nextFailed,
        status:
          nextCompleted + nextFailed >= prev.total_count
            ? "complete"
            : "running",
        error: results.find((r) => !r.ok)?.error ?? null,
      })
      .eq("id", runId);

    return { runId, results };
  });

/** Admin: hand-edit a draft before approving it. */
export const adminUpdateTownContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      id: string;
      intro?: string;
      local_conditions?: string;
      terrain_notes?: string;
      service_narrative?: string;
      nearby_context?: string;
      common_mistakes?: string[];
      faqs?: { question: string; answer: string }[];
    }) => input,
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const { id, ...fields } = data;
    const { error } = await context.supabase
      .from("town_content")
      .update({ ...fields, edited_manually: true })
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: approve or reject one or many rows. */
export const adminSetTownContentStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { ids: string[]; status: "approved" | "rejected" | "draft" }) => {
      if (!input.ids?.length) throw new Error("No rows selected");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const { error } = await context.supabase
      .from("town_content")
      .update({
        status: data.status,
        reviewed_by: context.userId,
        reviewed_by_email:
          (context.claims as { email?: string } | null)?.email ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .in("id", data.ids);
    if (error) throw new Error(error.message);

    // Audit trail: who changed the state of which town page, and when.
    const { data: touched } = await context.supabase
      .from("town_content")
      .select("state_slug,town_slug,service_slug,model")
      .in("id", data.ids);
    const actorEmail =
      (context.claims as { email?: string } | null)?.email ?? null;
    const rows = (touched ?? []) as {
      state_slug: string;
      town_slug: string;
      service_slug: string | null;
      model: string | null;
    }[];
    if (rows.length) {
      await context.supabase.from("town_content_audit").insert(
        rows.map((r) => ({
          state_slug: r.state_slug,
          town_slug: r.town_slug,
          service_keys: [r.service_slug ?? "town"],
          action: data.status,
          status: "success",
          model: r.model,
          sections_written: 1,
          actor_id: context.userId,
          actor_email: actorEmail,
        })),
      );
    }

    return { ok: true, count: data.ids.length };
  });

export interface TownContentAuditEntry {
  id: string;
  run_id: string | null;
  state_slug: string;
  town_slug: string;
  service_keys: string[];
  action: string;
  status: string;
  model: string | null;
  prompt_version: string | null;
  prompt_notes: string | null;
  sections_written: number;
  duration_ms: number | null;
  error: string | null;
  actor_email: string | null;
  created_at: string;
}

/** Admin: read the town-content audit log, newest first. */
export const adminListTownContentAudit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (
      input:
        { townSlug?: string; stateSlug?: string; limit?: number } | undefined,
    ) => input ?? {},
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    let query = context.supabase
      .from("town_content_audit")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(Math.min(data.limit ?? 100, 300));
    if (data.stateSlug) query = query.eq("state_slug", data.stateSlug);
    if (data.townSlug) query = query.eq("town_slug", data.townSlug);

    const { data: entries, error } = await query;
    if (error) throw new Error(error.message);
    return { entries: (entries ?? []) as unknown as TownContentAuditEntry[] };
  });

/* ------------------------------------------------------------------ *
 * Background queue
 *
 * Generation runs on the server now: the admin page enqueues work and
 * polls. Closing the tab no longer stops a run.
 * ------------------------------------------------------------------ */

export interface QueueItemInput {
  townSlug: string;
  townName?: string;
  /** One section group (e.g. ["town","dog-fence"]). */
  keys: string[];
}

export interface RunStatus {
  runId: string | null;
  stateSlug: string | null;
  total: number;
  done: number;
  failed: number;
  pending: number;
  processing: number;
  current: string | null;
  currentKeys: string[];
  error: string | null;
  active: boolean;
  startedAt: string | null;
}

/** Admin: queue generation work. Returns the run id to poll. */
export const adminEnqueueTownContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      stateSlug: string;
      items: QueueItemInput[];
      townCount: number;
      notes?: string | undefined;
      pageNotes?: Record<string, string> | undefined;
    }) => {
      if (!input.stateSlug) throw new Error("Missing state");
      if (!input.items?.length) throw new Error("Nothing to generate");
      if (input.items.length > 2000)
        throw new Error("Too many items in one run");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const actorEmail =
      (context.claims as { email?: string } | null)?.email ?? null;

    const { data: run, error: runError } = await context.supabase
      .from("town_content_runs")
      .insert({
        scope: data.stateSlug,
        state_slug: data.stateSlug,
        status: "running",
        total_count: data.townCount,
        model: "openai/gpt-5.6-sol",
        triggered_by: context.userId,
        triggered_by_email: actorEmail,
        notes: data.notes ?? null,
      })
      .select("id")
      .single();
    if (runError) throw new Error(runError.message);
    const runId = (run as { id: string }).id;

    // The last queued group of a town is the one that counts it as complete.
    const lastIndexForTown = new Map<string, number>();
    data.items.forEach((item, i) => lastIndexForTown.set(item.townSlug, i));

    const rows = data.items.map((item, i) => ({
      run_id: runId,
      state_slug: data.stateSlug,
      town_slug: item.townSlug,
      town_name: item.townName ?? item.townSlug,
      keys: item.keys,
      notes: data.notes?.trim() || null,
      page_notes: data.pageNotes ?? null,
      count_town: lastIndexForTown.get(item.townSlug) === i,
      status: "pending",
      position: i,
      triggered_by: context.userId,
      triggered_by_email: actorEmail,
    }));

    const { error } = await context.supabase
      .from("town_content_queue")
      .insert(rows);
    if (error) throw new Error(error.message);

    return { runId, queued: rows.length };
  });

/** Admin: progress for the newest run that still has queued work (or a given run). */
export const adminGetRunStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { runId?: string | null } | undefined) => input ?? {})
  .handler(async ({ data, context }): Promise<RunStatus> => {
    await requireAdmin(context);

    let runId = data.runId ?? null;
    if (!runId) {
      const { data: live } = await context.supabase
        .from("town_content_queue")
        .select("run_id")
        .in("status", ["pending", "processing"])
        .order("created_at", { ascending: false })
        .limit(1);
      runId =
        ((live ?? [])[0] as { run_id: string | null } | undefined)?.run_id ??
        null;
    }
    if (!runId) {
      // Nothing in flight — show the most recent run so failures stay visible.
      const { data: recent } = await context.supabase
        .from("town_content_queue")
        .select("run_id")
        .order("created_at", { ascending: false })
        .limit(1);
      runId =
        ((recent ?? [])[0] as { run_id: string | null } | undefined)?.run_id ??
        null;
    }

    const idle: RunStatus = {
      runId: null,
      stateSlug: null,
      total: 0,
      done: 0,
      failed: 0,
      pending: 0,
      processing: 0,
      current: null,
      currentKeys: [],
      error: null,
      active: false,
      startedAt: null,
    };
    if (!runId) return idle;

    const { data: items } = await context.supabase
      .from("town_content_queue")
      .select(
        "town_name,town_slug,keys,status,error,started_at,state_slug,position",
      )
      .eq("run_id", runId)
      .order("position", { ascending: true });

    const rows = (items ?? []) as {
      town_name: string | null;
      town_slug: string;
      keys: string[];
      status: string;
      error: string | null;
      started_at: string | null;
      state_slug: string;
      position: number;
    }[];
    if (!rows.length) return idle;

    const pending = rows.filter((r) => r.status === "pending").length;
    const processing = rows.filter((r) => r.status === "processing");
    const done = rows.filter((r) => r.status === "done").length;
    const failed = rows.filter((r) => r.status === "failed").length;
    const active = pending > 0 || processing.length > 0;
    const currentRow = processing[0] ?? null;
    const lastError = [...rows].reverse().find((r) => r.error)?.error ?? null;

    return {
      runId,
      stateSlug: rows[0]?.state_slug ?? null,
      total: rows.length,
      done,
      failed,
      pending,
      processing: processing.length,
      current: currentRow?.town_name ?? currentRow?.town_slug ?? null,
      currentKeys: currentRow?.keys ?? [],
      error: lastError,
      active,
      startedAt: currentRow?.started_at ?? null,
    };
  });

/** Admin: put every failed item of a run (or of a state) back in the queue. */
export const adminRetryFailedQueue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { runId?: string | null; stateSlug?: string | null } | undefined) =>
      input ?? {},
  )
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    let query = context.supabase
      .from("town_content_queue")
      .update({
        status: "pending",
        attempts: 0,
        error: null,
        started_at: null,
        finished_at: null,
      })
      .eq("status", "failed");
    if (data.runId) query = query.eq("run_id", data.runId);
    if (data.stateSlug) query = query.eq("state_slug", data.stateSlug);

    const { data: rows, error } = await query.select("id");
    if (error) throw new Error(error.message);
    return { requeued: (rows ?? []).length };
  });

/** Admin: stop a run — anything not yet started is dropped. */
export const adminCancelRun = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { runId: string }) => {
    if (!input.runId) throw new Error("Missing run");
    return input;
  })
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const { error } = await context.supabase
      .from("town_content_queue")
      .update({ status: "cancelled", finished_at: new Date().toISOString() })
      .eq("run_id", data.runId)
      .eq("status", "pending");
    if (error) throw new Error(error.message);

    await context.supabase
      .from("town_content_runs")
      .update({ status: "cancelled" })
      .eq("id", data.runId);
    return { ok: true };
  });

/** Admin: clear finished/failed queue rows for a state so the banner goes away. */
export const adminClearQueue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { stateSlug: string }) => input)
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const { error } = await context.supabase
      .from("town_content_queue")
      .delete()
      .eq("state_slug", data.stateSlug)
      .in("status", ["failed", "cancelled", "done"]);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
